import React from 'react';
import { FiMail, FiPhone, FiMapPin, FiMessageCircle } from 'react-icons/fi';

export const TermsPage = () => (
  <div className="section-padding">
    <div className="container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Terms of Service</h1>
      <p className="text-muted mb-4">Last updated: February 2026</p>

      <div className="dashboard-card mb-4">
        <h5>1. Acceptance of Terms</h5>
        <p>By accessing and using eBuyWiz, you agree to be bound by these Terms of Service. If you do not agree, please do not use our platform.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>2. User Accounts</h5>
        <p>You are responsible for maintaining the confidentiality of your account credentials. You must provide accurate information during registration and keep your account information up to date.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>3. Merchant Responsibilities</h5>
        <p>Merchants are responsible for the accuracy of product listings, timely order fulfillment, and compliance with applicable laws. eBuyWiz reserves the right to suspend accounts that violate our policies.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>4. Purchases & Payments</h5>
        <p>All purchases are subject to product availability. Prices are displayed in USD unless otherwise noted. Payments are processed securely through Stripe and Razorpay.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>5. Returns & Refunds</h5>
        <p>Return policies are set by individual merchants. Please review each merchant's return policy before purchasing. Disputes should be raised within 14 days of delivery.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>6. Limitation of Liability</h5>
        <p>eBuyWiz acts as a marketplace platform and is not directly responsible for products sold by merchants. We strive to ensure a safe shopping experience but cannot guarantee the quality of third-party products.</p>
      </div>
    </div>
  </div>
);

export const PrivacyPage = () => (
  <div className="section-padding">
    <div className="container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Privacy Policy</h1>
      <p className="text-muted mb-4">Last updated: February 2026</p>

      <div className="dashboard-card mb-4">
        <h5>Information We Collect</h5>
        <p>We collect information you provide directly: name, email, shipping address, payment details (processed by Stripe/Razorpay — we do not store card numbers), and browsing activity on our platform.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>How We Use Your Information</h5>
        <p>Your information is used to process orders, provide customer support, improve our platform, send transactional emails (order confirmations, shipping updates), and personalize your experience.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Data Sharing</h5>
        <p>We share necessary information with merchants to fulfill orders, and with payment processors for transactions. We do not sell your personal data to third parties.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Cookies & Tracking</h5>
        <p>We use cookies for authentication, preferences (language, theme), and recently viewed products. You can manage cookie preferences in your browser settings.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Your Rights</h5>
        <p>You can access, update, or delete your account data from your profile page. For data deletion requests, contact us at privacy@ebuywiz.com.</p>
      </div>
    </div>
  </div>
);

export const ContactPage = () => (
  <div className="section-padding">
    <div className="container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Contact Us</h1>
      <p className="text-muted mb-4">We'd love to hear from you. Reach out through any of these channels.</p>

      <div className="row g-4">
        <div className="col-md-6">
          <div className="dashboard-card h-100 text-center">
            <FiMail size={32} className="text-primary mb-3" />
            <h5>Email</h5>
            <p className="text-muted">support@ebuywiz.com</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="dashboard-card h-100 text-center">
            <FiPhone size={32} className="text-primary mb-3" />
            <h5>Phone</h5>
            <p className="text-muted">+91 44 1234 5678</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="dashboard-card h-100 text-center">
            <FiMapPin size={32} className="text-primary mb-3" />
            <h5>Address</h5>
            <p className="text-muted">Tiruvottiyūr, Chennai<br />Tamil Nadu, India</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="dashboard-card h-100 text-center">
            <FiMessageCircle size={32} className="text-primary mb-3" />
            <h5>Business Hours</h5>
            <p className="text-muted">Mon – Sat: 9 AM – 6 PM IST<br />Sunday: Closed</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const HelpPage = () => (
  <div className="section-padding">
    <div className="container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Help Center</h1>

      <div className="dashboard-card mb-3">
        <h5>How do I place an order?</h5>
        <p className="text-muted mb-0">Browse products, add items to your cart, proceed to checkout, enter your shipping details, and complete payment via Stripe or Razorpay.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I track my order?</h5>
        <p className="text-muted mb-0">Go to "My Orders" from your account menu. Click on any order to see the tracking timeline with real-time status updates and tracking numbers.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I apply a coupon?</h5>
        <p className="text-muted mb-0">Enter your coupon code in the "Coupon code" field on the cart page and click "Apply". The discount will be reflected in your order summary.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do loyalty points work?</h5>
        <p className="text-muted mb-0">You earn 1 point for every $1 spent. Once you have 100+ points, you can redeem them at checkout — 100 points = $1.00 discount.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I become a seller?</h5>
        <p className="text-muted mb-0">Register with a merchant account, set up your store in the Store Settings page, and start adding products. Your store will be activated after admin approval.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I return a product?</h5>
        <p className="text-muted mb-0">Return policies vary by merchant. Check the product page or order details for the specific return policy. Contact the merchant directly for return requests.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I bulk import products?</h5>
        <p className="text-muted mb-0">Merchants can go to "Bulk Import" in the sidebar, download the CSV template, fill in your products, and upload. All imported products start as drafts.</p>
      </div>

      <div className="text-center mt-4">
        <p className="text-muted">Still have questions?</p>
        <a href="/contact" className="btn btn-ebw-primary">Contact Support</a>
      </div>
    </div>
  </div>
);
